// frontend/src/utils/secureAxios.js
// FIXED: Added auth state reset on 401 errors

import axios from "axios";

// FIXED: Initialize logout flag to false on module load
// This ensures fresh page loads don't have stale flags
if (typeof window._webvenly_logout_in_progress === "undefined") {
  window._webvenly_logout_in_progress = false;
}

// Environment detection - Change
const isProduction = process.env.NODE_ENV === "production";
const isDevelopment = process.env.NODE_ENV === "development";
const hostname = window.location.hostname;
const isStaging = hostname === "stg.webvenly.com";
const isTraining = hostname === "trn.webvenly.com";
const isProductionEnv = hostname === "prd.webvenly.com";

// API Base URL configuration - WITH TEMPORARY STAGING FALLBACK
const getBaseURL = () => {
  const envApiUrl = process.env.REACT_APP_API_BASE_URL?.trim();

  // ðŸ† PRIORITY 1: Environment variable
  if (envApiUrl) {
    console.log("âœ… Using REACT_APP_API_BASE_URL:", envApiUrl);
    return envApiUrl;
  }

  // ðŸš¨ TEMPORARY FALLBACK: Fix staging while you sort out env vars
  console.warn(
    "âš ï¸ REACT_APP_API_BASE_URL not found, using fallback for:",
    hostname
  );

  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:5000";
  }

  if (hostname === "stg.webvenly.com") {
    console.warn(
      "ðŸ”§ TEMPORARY: Using hardcoded staging URL - FIX YOUR ENV VAR!"
    );
    return "https://webvenly-backend-staging-823753138069.us-east1.run.app";
  }

  if (hostname === "trn.webvenly.com") {
    return "https://webvenly-backend-trn-823753138069.us-east1.run.app";
  }

  if (hostname === "prd.webvenly.com") {
    return "https://webvenly-backend-prd-823753138069.us-east1.run.app";
  }

  // ðŸš¨ ERROR: Unknown environment
  throw new Error(
    `âŒ CONFIGURATION ERROR: Set REACT_APP_API_BASE_URL environment variable for hostname: ${hostname}`
  );
};

// Create axios instance with basic configuration
const secureAxios = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  withCredentials: true, // Always send cookies for primary auth
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Cross-origin detection
const isCrossOrigin = () => {
  const baseURL = getBaseURL();
  return (
    !baseURL.includes("localhost") &&
    !baseURL.includes(window.location.hostname)
  );
};

// Token management utilities
const getStoredToken = () => {
  try {
    return sessionStorage.getItem("webvenly_auth_token");
  } catch (error) {
    console.warn("Failed to retrieve stored token:", error);
    return null;
  }
};

const hasAuthToken = () => {
  // Check for sessionStorage token
  const storedToken = getStoredToken();
  if (storedToken) return true;

  // Check for auth cookie (basic check)
  return document.cookie.includes("token=");
};

console.log("SecureAxios Configuration:", {
  baseURL: getBaseURL(),
  environment: process.env.NODE_ENV,
  hostname: window.location.hostname,
  isCrossOrigin: isCrossOrigin(),
  withCredentials: true,
});

// ENHANCED REQUEST INTERCEPTOR - Handles both cookies and Bearer tokens
secureAxios.interceptors.request.use(
  (config) => {
    // FIXED: Handle FormData uploads - let browser set Content-Type with boundary
    if (config.data instanceof FormData) {
      // Remove Content-Type so browser can set it with correct boundary
      delete config.headers["Content-Type"];
      if (isDevelopment) {
        console.log("FormData detected - allowing browser to set Content-Type");
      }
    }

    // Add Bearer token from sessionStorage if available and no auth header exists
    const storedToken = getStoredToken();
    const existingAuth =
      config.headers?.Authorization || config.headers?.authorization;

    if (storedToken && !existingAuth) {
      config.headers.Authorization = `Bearer ${storedToken}`;
      console.log("📤 Request interceptor: Added token from sessionStorage", {
        url: config.url,
        tokenPreview: storedToken.substring(0, 20) + "...",
      });
    } else if (existingAuth) {
      console.log(
        "📤 Request interceptor: Using existing Authorization header",
        {
          url: config.url,
          hasAuth: true,
        }
      );
    } else {
      console.log("📤 Request interceptor: No token available", {
        url: config.url,
        storedToken: !!storedToken,
        existingAuth: !!existingAuth,
      });
    }

    // FIXED: Add X-User-Activity header for user-initiated requests that should extend session
    const isBackgroundRequest =
      config.headers?.["X-Background-Request"] === "true";
    const isWidgetRequest =
      config.url?.includes("/widgets/") ||
      config.url?.includes("/dashboard-widgets/");
    const isHeartbeatRequest =
      config.url?.includes("/api/auth/activity") ||
      config.url?.includes("/api/auth/heartbeat");

    // Mark user-initiated requests that should extend server session
    if (!isBackgroundRequest && !isWidgetRequest && !isHeartbeatRequest) {
      config.headers["X-User-Activity"] = "true";
      if (isDevelopment) {
        console.log("Added X-User-Activity header for session extension");
      }
    }

    // Add debug headers
    if (!isProductionEnv && (isStaging || isTraining || isDevelopment)) {
      const envName = isStaging
        ? "staging"
        : isTraining
        ? "training"
        : "development";
      config.headers["X-Debug-Frontend"] = envName;
      config.headers["X-Frontend-Origin"] = window.location.origin;
    }

    // Debug logging in development
    if (isDevelopment) {
      const hasAuth = !!(config.headers.Authorization || hasAuthToken());
      console.log("API Request:", {
        method: config.method?.toUpperCase(),
        url: config.url,
        hasAuth: hasAuth,
        crossOrigin: isCrossOrigin(),
        authMethod: config.headers.Authorization ? "Bearer" : "Cookie",
        willExtendSession: config.headers["X-User-Activity"] === "true",
      });
    }

    return config;
  },
  (error) => {
    console.error("Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// ENHANCED RESPONSE INTERCEPTOR - Handles token updates and errors
secureAxios.interceptors.response.use(
  (response) => {
    // Store new token if provided in response
    if (response.data?.token) {
      try {
        sessionStorage.setItem("webvenly_auth_token", response.data.token);
        if (isDevelopment) {
          console.log("Updated stored token from response");
        }
      } catch (error) {
        console.warn("Failed to store updated token:", error);
      }
    }

    // Debug logging in development
    if (isDevelopment) {
      console.log("API Response:", {
        status: response.status,
        url: response.config?.url,
        hasData: !!response.data,
        hasNewToken: !!response.data?.token,
      });
    }

    return response;
  },
  (error) => {
    // FIXED: Handle 401 errors with proper endpoint filtering and logout protection
    if (error.response?.status === 401) {
      try {
        const errorCode = error.response?.data?.code || "SESSION_EXPIRED";
        const errorMessage = error.response?.data?.error || "Session expired";
        const requestUrl = error.config?.url || "";

        // Don't trigger logout for auth endpoints or if logout already in progress
        const isAuthEndpoint =
          requestUrl.includes("/api/auth/logout") ||
          requestUrl.includes("/api/auth/refresh") ||
          requestUrl.includes("/api/auth/activity");
        const globalLogoutInProgress = window._webvenly_logout_in_progress;

        // FIXED: Check if we're in a login grace period (prevents clearing tokens during login race condition)
        const loginGraceTimestamp = window._webvenly_login_timestamp;
        const isInLoginGracePeriod =
          loginGraceTimestamp && Date.now() - loginGraceTimestamp < 3000; // 3 second grace period

        if (isAuthEndpoint) {
          console.log(
            `401 from auth endpoint (${requestUrl}) - ignoring to prevent loops`
          );
        } else if (globalLogoutInProgress) {
          console.log("401 during logout process - ignoring to prevent loops");
        } else if (isInLoginGracePeriod) {
          // FIXED: During login grace period, retry the request after a short delay
          // This handles race conditions where API calls fire before token is attached
          const timeSinceLogin = Date.now() - loginGraceTimestamp;
          const retryCount = error.config._retryCount || 0;

          console.log(
            `401 during login grace period (${Math.round(
              timeSinceLogin / 1000
            )}s since login, retry ${retryCount})`
          );

          // Only retry up to 3 times
          if (retryCount < 3) {
            // Wait and retry - token should be available by then
            return new Promise((resolve, reject) => {
              setTimeout(() => {
                // Re-read token from sessionStorage
                const token = sessionStorage.getItem("webvenly_auth_token");

                if (token) {
                  // Create a fresh config with the token properly set
                  const retryConfig = {
                    ...error.config,
                    _retryCount: retryCount + 1,
                    headers: {
                      ...error.config.headers,
                      Authorization: `Bearer ${token}`,
                    },
                  };

                  console.log(
                    `Retrying request with token (attempt ${retryCount + 1})`,
                    {
                      url: retryConfig.url,
                      hasToken: !!token,
                      tokenPreview: token
                        ? token.substring(0, 20) + "..."
                        : "none",
                    }
                  );

                  resolve(secureAxios.request(retryConfig));
                } else {
                  console.log(
                    `No token available for retry (attempt ${retryCount + 1})`
                  );
                  reject(error);
                }
              }, 250 * (retryCount + 1)); // 250ms, 500ms, 750ms delays
            });
          } else {
            console.log(`Max retries reached during grace period`);
          }
        } else {
          console.log(
            `401 error detected: ${errorCode} - ${errorMessage} from ${requestUrl}`
          );

          // Set global flag first
          window._webvenly_logout_in_progress = true;

          // Clear stored tokens
          sessionStorage.removeItem("webvenly_auth_token");
          sessionStorage.removeItem("user_session");

          // Dispatch logout event with delay to prevent race conditions
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent("auth-logout", {
                detail: {
                  reason: "session_expired",
                  code: errorCode,
                  message: errorMessage,
                  url: requestUrl,
                  isTimeout: true,
                },
              })
            );
          }, 50);

          if (isDevelopment) {
            console.log(
              "401 error - cleared tokens and dispatched logout event"
            );
          }
        }
      } catch (clearError) {
        console.warn("Failed to handle 401 error properly:", clearError);
      }
    }

    // Enhanced error logging in development
    if (isDevelopment) {
      console.error("API Error Details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        data: error.response?.data,
        hasAuth: !!(error.config?.headers?.Authorization || hasAuthToken()),
      });
    }

    // User-friendly error messages
    if (error.response?.status === 401) {
      console.log("401 Unauthorized - authentication required");
    } else if (error.response?.status === 403) {
      console.log("403 Forbidden - insufficient permissions");
    } else if (error.response?.status >= 500) {
      console.error("Server error:", error.response?.status);
    } else if (error.code === "NETWORK_ERROR" || !error.response) {
      console.error("Network error - check connection");
    }

    return Promise.reject(error);
  }
);

// Legacy function compatibility - deprecated but maintained for backward compatibility
export const registerAuthResetFunction = (resetFunction) => {
  if (isDevelopment) {
    console.log(
      "registerAuthResetFunction is deprecated in simplified version"
    );
  }
  // No-op for compatibility
};

// Export utilities
export { getStoredToken, hasAuthToken, isCrossOrigin };
export default secureAxios;
